import React, { useState } from 'react';
import { Form, Button, Space, Alert, Spin } from 'antd';
import { message } from '../services/toastMessage';
import { useTranslation } from 'react-i18next';
import { changePassword } from '../services/AuthenticationService';
import PasswordStrengthMeter from '././PasswordStrengthMeter';
import FloatingLabelInput from './FloatingLabelInput';
import { useAuth } from '../contextProviders/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';

interface ChangePasswordFormValues {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

const ChangePasswordForm: React.FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [newPassword, setNewPassword] = useState('');
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handlePasswordChange = async (values: ChangePasswordFormValues) => {
    if (passwordStrength < 60) {
      message.warning(translate(t, TRANSLATIONS.CHANGE_PASSWORD_WEAK_PASSWORD_WARNING));
      return;
    }

    setLoading(true);
    try {
      await changePassword(
        values.currentPassword,
        values.newPassword,
        values.confirmNewPassword
      );

      message.success({
        content: translate(t, TRANSLATIONS.CHANGE_PASSWORD_SUCCESS_MESSAGE),
        duration: 3,
      });

      form.resetFields();

      setTimeout(() => {
        logout();
        navigate('/login');
      }, 2000);

    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.CHANGE_PASSWORD_ERROR_FALLBACK));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Spin spinning={loading}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Alert
          message={translate(t, TRANSLATIONS.CHANGE_PASSWORD_LOGOUT_ALERT_MESSAGE)}
          description={translate(t, TRANSLATIONS.CHANGE_PASSWORD_LOGOUT_ALERT_DESCRIPTION)}
          type="info"
          showIcon
          closable
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={handlePasswordChange}
          disabled={loading}
        >
          <Form.Item
            name="currentPassword"
            rules={[{ required: true, message: translate(t, TRANSLATIONS.CHANGE_PASSWORD_CURRENT_REQUIRED) }]}
          >
            <FloatingLabelInput label={translate(t, TRANSLATIONS.CHANGE_PASSWORD_CURRENT_LABEL)} password />
          </Form.Item>

          <Form.Item
            name="newPassword"
            rules={[
              { required: true, message: translate(t, TRANSLATIONS.CHANGE_PASSWORD_NEW_REQUIRED) },
              { min: 8, message: translate(t, TRANSLATIONS.CHANGE_PASSWORD_NEW_MIN_LENGTH) },
              {
                pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                message: translate(t, TRANSLATIONS.CHANGE_PASSWORD_NEW_PATTERN),
              },
            ]}
          >
            <FloatingLabelInput
              label={translate(t, TRANSLATIONS.CHANGE_PASSWORD_NEW_LABEL)}
              password
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </Form.Item>

          {newPassword && (
            <PasswordStrengthMeter
              password={newPassword}
              onStrengthChange={setPasswordStrength}
            />
          )}

          <Form.Item
            name="confirmNewPassword"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: translate(t, TRANSLATIONS.CHANGE_PASSWORD_CONFIRM_REQUIRED) },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(translate(t, TRANSLATIONS.CHANGE_PASSWORD_CONFIRM_MISMATCH)));
                },
              }),
            ]}
          >
            <FloatingLabelInput label={translate(t, TRANSLATIONS.CHANGE_PASSWORD_CONFIRM_LABEL)} password />
          </Form.Item>

          <Form.Item style={{ marginTop: 24 }}>
            <Button type="primary" htmlType="submit" size="large" block style={{ borderRadius: 999, height: 48 }}>
              {translate(t, TRANSLATIONS.CHANGE_PASSWORD_TITLE)}
            </Button>
          </Form.Item>
        </Form>
      </Space>
    </Spin>
  );
};

export default ChangePasswordForm;
